import { useCallback } from 'react';
import { useGoogleLogin } from '@react-oauth/google';
import { authenticateWithGoogle, UserProfile } from '../services/api';
import { Analytics } from '../services/analytics';
import { useCompleteSignIn } from './useCompleteSignIn';

interface UseGoogleSignInProps {
  onProfileUpdate: (profile: UserProfile) => void;
  onSuccess?: () => void;
  onError?: (message: string) => void;
}

export const useGoogleSignIn = ({ onProfileUpdate, onSuccess, onError }: UseGoogleSignInProps) => {
  const completeSignIn = useCompleteSignIn(onProfileUpdate, onSuccess);
  const handleLoginSuccess = useCallback(async (tokenResponse: any) => {
    try {
      const { token, isNewAccount } = await authenticateWithGoogle(tokenResponse.access_token);
      await completeSignIn(token, 'google', isNewAccount);
    } catch (error: any) {
      Analytics.logAuth.signIn('google', false);
      const message = error.response?.data?.error;
      onError?.(typeof message === 'string' ? message : 'Google sign-in failed. Please try again.');
    }
  }, [completeSignIn, onError]);

  return useGoogleLogin({
    onSuccess: handleLoginSuccess,
    onError: () => onError?.('Google sign-in failed. Please try again.'),
    onNonOAuthError: (error) => {
      if (error.type === 'popup_failed_to_open') {
        onError?.('Google sign-in could not open. Allow pop-ups for this site, or continue with email.');
      } else if (error.type === 'popup_closed') {
        onError?.('Google sign-in did not finish. If this keeps happening, open Eatsy directly in Chrome or continue with email.');
      } else {
        onError?.('Google sign-in could not finish. Please try again or continue with email.');
      }
    },
    flow: 'implicit',
  });
};
